<template>
  <div class="v-window-item py-6 px-3 py-md-12 px-md-6 px-lg-12 v-window-item--active justify-center" style="min-width: 100%; height: 100%">
    <div class="mx-auto v-sheet theme--light" style="min-width: 50%; max-width: 1230px">
      <Loader :messageSelector="messageSelector" :overlay="overlay" />
      <v-form ref="form" v-model="valid">
        <fieldset>
          <legend class="text-h6 text-sm-h5">Demande au service à la clientèle</legend>
          <p>
            L'Ordre des travailleurs sociaux et des thérapeutes conjugaux et familiaux du Québec vous demande de fournir les renseignements personnels
            prévus au présent formulaire dans le cadre de ses fonctions, afin de traiter votre demande. Conformément aux lois qui encadrent la
            protection des renseignements personnels au Québec, l'Ordre ne collecte que les renseignements nécessaires aux fins pour lesquelles ils
            sont collectés. Pour en savoir plus, veuillez consulter la
            <a href="https://www.otstcfq.org/politique-de-confidentialite/#confidentialite" target="_blank" class="link-trim-space">
              Politique de confidentialité
            </a>
            adoptée par l'Ordre.
          </p>
          <v-container>
            <v-row>
              <!-- row 1 -->
              <v-col sm="12" md="6">
                <v-text-field v-model="request.lastName" label="Nom"></v-text-field>
              </v-col>
              <v-col sm="12" md="6">
                <v-text-field v-model="request.firstName" label="Prénom" class="col-6"></v-text-field>
              </v-col>
              <!-- row2 -->
              <v-col sm="12" md="6">
                <!-- Code client ou numéro permis -->
                <v-text-field v-model="request.codeClient" label="Code client ou numéro permis"></v-text-field>
              </v-col>
              <v-col sm="12" md="6">
                <v-text-field
                  v-model="request.courriel"
                  type="email"
                  :rules="rules.courriel"
                  label="Courriel"
                  required
                  class="cssRequired"
                ></v-text-field>
              </v-col>
              <!-- row 3 -->
              <v-col sm="12" md="6">
                <v-phone-input v-model="request.telephone" label="Téléphone"></v-phone-input>
              </v-col>
              <v-col sm="12" md="6">
                <v-text-field v-model="request.poste" label="Poste"></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <!-- Sujet demande -->
                <label class="v-label theme--light cssRequired">Sujet de la demande</label>

                <ckeditor v-model="request.content"
                          :config="editorConfig"/>

                <v-input :value="request.content" :rules="rules.content"></v-input>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <!-- Fichiers -->
                <FileUploader
                  ref="fileUploader"
                  label="Fichiers associés à la demande"
                  :multiple="true"
                  :minRequired="0"
                  :rule="rules.files"
                  @filesUploaded="processUpload($event)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-snackbar :timeout="10_000" :value="successTransmission" absolute color="success"> Votre demande a bien été transmise. </v-snackbar>
              </v-col>
            </v-row>
          </v-container>
        </fieldset>
      </v-form>

      <div class="text-right">
        <v-btn color="primary" :disabled="!valid" @click="save()">Soumettre votre demande</v-btn>
      </div>
    </div>

    <v-snackbar v-model="errorBar.error" elevation="24" color="blue-grey" timeout="-1" multi-line class="mb-8">
      {{ errorBar.message }}<br />
      {{ errorBar.detailMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn color="warning" v-bind="attrs" @click="errorBar.error = false"> {{ $t("error.close") }} </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from "vuex"
import Loader from "@/components/Loader.vue"
import Attachment from "@/components/attachments/Attachment.vue"
import { PublicApi } from "@/plugins/api"
import FileUploader from "@/components/common/FileUploader.vue"
import CKEditor from 'ckeditor4-vue'

export default {
  components: {
    FileUploader,
    Attachment,
    Loader,
    ckeditor: CKEditor.component,
  },
  data() {
    return {
      editorConfig: {
        versionCheck: false, // disable notification about new commercial version
        toolbarGroups: [
          { name: 'clipboard', groups: [ 'clipboard', 'undo' ] },
          { name: 'editing', groups: [ 'find', 'selection', 'spellchecker', 'editing' ] },
          { name: 'forms', groups: [ 'forms' ] },
          { name: 'tools', groups: [ 'tools' ] },
          { name: 'document', groups: [ 'mode', 'document', 'doctools' ] },
          { name: 'others', groups: [ 'others' ] },
          { name: 'basicstyles', groups: [ 'basicstyles', 'cleanup' ] },
          { name: 'links', groups: [ 'links' ] },
          { name: 'insert', groups: [ 'insert' ] },
          { name: 'paragraph', groups: [ 'list', 'indent', 'blocks', 'align', 'bidi', 'paragraph' ] },
          { name: 'styles', groups: [ 'styles' ] },
          { name: 'colors', groups: [ 'colors' ] },
          { name: 'about', groups: [ 'about' ] },
        ],
        removeButtons: 'Subscript,Superscript,Cut,Copy,Scayt,Anchor,HorizontalRule,Maximize,Source,About',
      },
      successTransmission: false,
      overlay: false,
      messageSelector: "",
      valid: false,
      errorBar: {
        error: false,
        message: null,
        detailMessage: null
      },
      request: {
        contactId: "",
        lastName: "",
        firstName: "",
        codeClient: "",
        courriel: "",
        telephone: "",
        poste: "",
        content: ""
      },
      rules: {
        requis: [(v) => !!v || "Cette valeur est requise"],
        courriel: [(v) => !!v || "Le courriel est requis", (v) => this.validateEmail(v) || "Le format du courriel est invalide"],
        content: [(v) => !!v || "Le contenu de la demande est requis"],
        files: [(v) => this.validateFiles(v)]
      }
    }
  },
  async mounted() {
    if (this.$route.params.contactId != null) {
      this.request.contactId = this.$route.params.contactId

      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const res = await PublicApi.get(`sac-request/contact/${this.request.contactId}`)

        this.request = Object.assign(this.request, res.data)

        this.validateForm(true)
      } catch (erreur) {
        this.handleError(erreur)
      } finally {
        this.overlay = false
      }
    }
  },
  methods: {
    ...mapActions({}),
    validateEmail(courriel) {
      return this.$regexCourriel.test(courriel)
    },
    async save() {
      this.validateForm()

      if (this.valid) {
        this.messageSelector = "Save"
        this.overlay = true

        await PublicApi.postForm(`sac-request`, this.request, {
          formSerializer: {
            indexes: true // array indexes format: null - no brackets, false - empty brackets, true - brackets with indexes
          }
        })
          .then(() => {
            this.overlay = false
            this.successTransmission = true
            this.resetForm()
          })
          .catch((reason) => {
            this.handleError(reason)
            this.overlay = false
          })
      }
    },
    validateForm(resetValidation = false) {
      this.valid = this.$refs.form.validate()

      if (resetValidation) {
        this.$refs.form?.resetValidation()
      }
    },
    processUpload(uploadedFiles) {
      this.request.files = uploadedFiles
    },
    validateFiles(files) {
      if (!files || files.length === 0) {
        // pas de fichiers => ok
        return true
      }

      const totalSize = files.reduce((accumulator, currentValue) => accumulator + currentValue?.size, 0)

      return totalSize < 30 * 1024 * 1024 || "La taille des fichiers doit être inférieure à 30 Mo"
    },
    handleError(detail = null) {
      this.errorBar.error = true
      this.errorBar.message = this.$t("error.message")
      this.errorBar.detailMessage = detail || ""
    },
    resetForm() {
      for (let prop in this.request) {
        this.request[prop] = ""
      }
      this.$refs.fileUploader.clearFiles()
      this.$refs.form?.resetValidation()
    }
  }
}
</script>